import { CommonModule } from '@angular/common';
import {
  Component, Input, Output, EventEmitter,
  signal, computed, effect,
  OnInit,
  Renderer2,
  ElementRef,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigInput } from '../../interfaces/interfaces';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
})
export class InputComponent implements OnInit, AfterViewInit {

  @Input() set config(value: ConfigInput) {
    this.configSignal.set({
      label: value.label || 'Item',
      subLabel: value.subLabel || '',
      formName: value.formName,
      placeholder: value.placeholder || 'Ingrese un valor',
      type: value.type || 'text',
      defaultValue: value.defaultValue ?? '',
      enabled: value.enabled ?? true,
      validation: value.validation || null,
      textError: value.textError,
    });
  }

  @Input() timeoutSeconds: number = 8;

  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  configSignal = signal<ConfigInput>({
    label: 'Item',
    subLabel: '',
    placeholder: 'Ingrese un valor',
    type: 'text',
    defaultValue: null,
    enabled: true,
    validation: null,
    textError: 'Este campo es requerido.'
  });

  formattedValue = signal<string>('');

  enabled = computed(() => this.configSignal().enabled);
  currentValue = computed(() => this.configSignal().defaultValue);
  
  emitOnlyOnBlur = computed(() => {
    const formName:any = this.configSignal().formName;
    const blurOnlyFields = ['income', 'address'];
    return blurOnlyFields.includes(formName);
  });

  form: FormGroup;

  offTimeOut = signal(false);
  hasWritten = signal(false);
  valueSignal = signal<any>('');
  lastEmittedValue = signal<any>(null);

  hasSpecificError = computed(() => {
    const c = this.itemControl();
    if (!c || !c.errors) return false;
    return (
      c.hasError('maxDateYesterdayColombia') ||
      c.hasError('maxDateTodayColombia') ||
      c.hasError('min')
    );
  });

  hasGenericError = computed(() => {
    const c = this.itemControl();
    if (!c || !c.errors) return false;
    return (
      c.hasError('required') ||
      c.hasError('minlength') ||
      c.hasError('maxlength') ||
      c.hasError('pattern')
    );
  });

  private viewInitialized = signal(false);
  private debounceTimer: any;
  private timeoutHandle: any;

  @Output() submitValue = new EventEmitter<any>();
  @Output() fieldStateChange = new EventEmitter<{ formName?: string; value: string | number | null; valid: boolean }>();

  constructor(private renderer: Renderer2) {
    this.form = new FormGroup({
      item: new FormControl('', Validators.compose(this.configSignal().validation ?? []))
    });

    /* Habilitar / deshabilitar dinámicamente */
    effect(() => {
      const control = this.form.get('item');
      this.enabled() ? control?.enable() : control?.disable();

      if (this.enabled()) {
        this.getStyleEnabled();
      } else {
        this.getStyleTextDisabled();
      }
    });

    /* Cambiar validaciones dinámicamente */
    effect(() => {
      const control = this.form.get('item');
      const validations = this.configSignal().validation;
      control?.setValidators(Validators.compose(validations ?? []));
      control?.updateValueAndValidity();
    });

    /* Setear valor por defecto */
    effect(() => {
      const control = this.form.get('item');
      if (this.currentValue() !== undefined && control?.value !== this.currentValue()) {
        control?.setValue(this.currentValue());
      }
    });

    /* Cambios de valor */
    this.form.get('item')?.valueChanges.subscribe((val) => {

      if (this.configSignal().type === 'number' && val !== null && val !== '') {
        const numericValue = val.toString().replace(/,/g, '');
        if (!isNaN(numericValue)) {
          const formatted = this.formatNumberWithCommas(numericValue);
          this.formattedValue.set(formatted);

          setTimeout(() => {
            this.form.get('item')?.setValue(numericValue, { emitEvent: false });
            this.emitFieldStateChange();
          });

          this.valueSignal.set(numericValue);
        }
      } else {
        this.valueSignal.set(val);
        this.formattedValue.set(val);
      }

      if (val !== '' && val !== null && val !== undefined) {
        this.hasWritten.set(true);
      }

      this.startTimeout();
      
      // Solo resetear debounce si NO es un campo que emite solo en blur
      if (!this.emitOnlyOnBlur()) {
        this.resetDebounce();
      }

      this.updateStylesByState();
      this.emitFieldStateChange();
    });

    /* Si se vuelve válido: apagar timeout y errores */
    effect(() => {
      const valid = this.itemControl()?.valid;
      if (valid) {
        this.offTimeOut.set(false);
        clearTimeout(this.timeoutHandle);
        this.getStyleEnabled();
      }
    });
  }

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
  }

  itemControl: any = computed(() => this.form.get('item'));

  private applyInitialStyles() {
    if (this.enabled()) {
      this.getStyleEnabled();
    } else {
      this.getStyleTextDisabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  private startTimeout() {
    clearTimeout(this.timeoutHandle);

    const control = this.form.get('item');

    // Si el mensaje ya está visible, actualizar de inmediato (sin re-esperar el delay).
    if (this.offTimeOut()) {
      if (control?.invalid) {
        this.getStyleError();
      } else {
        this.offTimeOut.set(false);
      }
      return;
    }

    this.offTimeOut.set(false);

    this.timeoutHandle = setTimeout(() => {
      const currentControl = this.form.get('item');
      if (currentControl && currentControl.invalid) {
        this.offTimeOut.set(true);
        this.getStyleError();
      }
    }, this.timeoutSeconds * 1000);
  }

  private emitFieldStateChange(): void {
    const control = this.form.get('item');
    if (!control) return;

    let value: string | number | null = control.value ?? null;
    if (value === '') {
      value = null;
    }

    if (this.configSignal().type === 'number' && value !== null && value !== '') {
      value = value.toString().replace(/,/g, '');
    }

    this.fieldStateChange.emit({
      formName: this.configSignal().formName,
      value,
      valid: control.valid,
    });
  }

  getCurrentValue(): { formName: string | undefined; value: string | number | null; valid: boolean } {
    const control = this.form.get('item');
    let value: string | number | null = control?.value ?? null;

    if (value === '') {
      value = null;
    }

    if (this.configSignal().type === 'number' && value !== null && value !== '') {
      value = value.toString().replace(/,/g, '');
    }

    return {
      formName: this.configSignal().formName,
      value,
      valid: !!control?.valid,
    };
  }

  forceShowValidation(): void {
    this.offTimeOut.set(true);
    this.getStyleError();
  }

  emit() {
    let valueToEmit = this.form.value.item;

    if (this.configSignal().type === 'number' && valueToEmit) {
      valueToEmit = valueToEmit.toString().replace(/,/g, '');
    }

    const hasValueChanged = valueToEmit !== this.lastEmittedValue();

    if ((this.form.valid || valueToEmit == '') && hasValueChanged) {
      this.lastEmittedValue.set(valueToEmit);
      this.submitValue.emit({ formName: this.configSignal().formName, value: valueToEmit });
    }

    if (!this.form.valid) {
      this.lastEmittedValue.set(valueToEmit);
      this.submitValue.emit({ formName: this.configSignal().formName, value: null });
    }
  }

  resetDebounce() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.emit();
    }, 700);
  }

  onBlurInput() {
    // Para TODOS los campos, emitir en blur
    this.emit();

    const control = this.form.get('item');
    if (control?.invalid) {
      this.forceShowValidation();
    }

    // Si es un campo que solo emite en blur, también limpiar el timer de debounce si existe
    if (this.emitOnlyOnBlur()) {
      clearTimeout(this.debounceTimer);
    }
  }

  private formatNumberWithCommas(value: string): string {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  onNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/,/g, '');

    if (/^\d*$/.test(value)) {
      this.form.get('item')?.setValue(value);
    } else {
      input.value = this.formattedValue();
    }
  }

  private updateStylesByState() {
    const control = this.form.get('item');
    if (!control) return;

    if (!this.enabled()) {
      this.getStyleTextDisabled();
      return;
    }

    if (control.invalid) {
      this.getStyleError();
      return;
    }

    this.getStyleEnabled();
  }

  getStyleError() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error');
      }
    });
  }

  getStyleEnabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled');
      }
    });
  }

  getStyleTextDisabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled');
      }
    });
  }
}